import type { CityConfig } from "@/pages/city-template/types";

export const eugeneConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from Oregon Vital Records in Portland. Online, mail, or in-person at 800 NE Oregon St, Suite 205.",
      url: "https://www.oregon.gov/oha/ph/birthdeathcertificates/",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through Oregon Center for Health Statistics. Available online and by mail for estate and legal matters.",
      url: "https://www.oregon.gov/oha/ph/birthdeathcertificates/",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage licenses from the Lane County Clerk at 275 W 10th Ave in Eugene. Certified copies for passport name changes and travel documentation.",
      url: "https://www.lanecounty.org/government/county_departments/county_clerk",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through Lane County Circuit Court. Required for name change passport corrections.",
      url: "https://www.courts.oregon.gov/courts/lane",
    },
  ],
  city: "Eugene",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "eugene",
  route: "/cities/eugene",
  title: "Emergency Passport Eugene, OR — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Eugene, OR — as fast as 24 hours. Fast passport renewal, child passport expediting, and same-day help for Eugene, Springfield, and Lane County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Eugene, Oregon",
  heroSubtitle: "Passport expired? Trip coming up fast? Fast Passport Center helps Eugene and Lane County travelers get their passports quickly — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Eugene%20Oregon%20skyline%20with%20Willamette%20River%20in%20foreground%2C%20lush%20green%20Cascade%20foothills%20and%20dense%20Pacific%20Northwest%20forest%20in%20background%2C%20warm%20afternoon%20golden%20light%20illuminating%20the%20valley%2C%20clear%20blue%20sky%20with%20light%20clouds%2C%20peaceful%20university%20town%20atmosphere%2C%20photorealistic%20landscape%20photography%2C%20vibrant%20Pacific%20Northwest%20colors%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=eugene-hero-01&orientation=landscape",
  heroImageAlt: "Eugene Oregon cityscape with Willamette River and Cascade foothills",
  geoPosition: "44.0521;-123.0868",
  geoPlacename: "Eugene, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "Approximately 285 miles, 4.5 hours via I-5",
  },
  nearbyCitiesZips: [
    { name: "Eugene", zips: "97401–97408" },
    { name: "Springfield", zips: "97477–97478" },
    { name: "Cottage Grove", zips: "97424" },
    { name: "Junction City", zips: "97448" },
    { name: "Creswell", zips: "97426" },
    { name: "Coburg", zips: "97408" },
    { name: "Veneta", zips: "97487" },
    { name: "Elmira", zips: "97437" },
    { name: "Oakridge", zips: "97463" },
    { name: "Florence", zips: "97439" },
    { name: "Harrisburg", zips: "97446" },
    { name: "Monroe", zips: "97456" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home in Eugene or Springfield in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Eugene", desc: "FedEx and UPS locations in Eugene offer overnight shipping. Most clients ship same-day and documents arrive next morning." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission — essential for urgent passport renewal in Eugene where there is no local regional agency." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Ryan H.",
      location: "Eugene, OR",
      rating: 5,
      text: "I'm a graduate student at UO and needed an emergency passport for a research trip to Germany. Three weeks, expired passport. Fast Passport Center got me renewed in 7 days — I shipped from the FedEx on Valley River Way. Couldn't have done it without them.",
      service: "Passport Renewal",
    },
    {
      name: "Linda P.",
      location: "Springfield, OR",
      rating: 5,
      text: "We needed a passport for our 10-year-old before a cruise to Mexico. Being in Springfield, I had no idea where to start. Fast Passport Center walked us through the child passport process step by step — both parents signed off and we had it in 9 days.",
      service: "Child Passport",
    },
    {
      name: "Travis M.",
      location: "Cottage Grove, OR",
      rating: 5,
      text: "Lost my passport a week before a business trip to Canada. Fast Passport Center guided me through the DS-64 form, what documents to gather, and expedited the replacement in 5 days. Living in Cottage Grove I thought I was stuck. They saved the trip.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Sarah K.",
      location: "Eugene, OR",
      rating: 5,
      text: "Needed a name change on my passport after getting married — and we were leaving for our honeymoon in Italy in 12 days. Fast Passport Center handled the DS-5504 and had the corrected passport back in 4 days. They made it completely stress-free.",
      service: "Name Change Passport",
    },
  ],
  facilities: [
    {
      id: "eugene-main-post",
      name: "Eugene Main Post Office",
      type: "USPS Passport Acceptance",
      address: "520 Willamette St",
      city: "Eugene",
      state: "OR",
      zip: "97401",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Eugene",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Eugene. Appointment required — book online at usps.com. Photo services typically available.",
      icon: "ri-mail-line",
    },
    {
      id: "eugene-river-road",
      name: "Eugene River Road Post Office",
      type: "USPS Passport Acceptance",
      address: "1120 River Rd",
      city: "Eugene",
      state: "OR",
      zip: "97404",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "North Eugene",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Eugene passport acceptance option. Confirm appointment and photo service availability.",
      icon: "ri-mail-line",
    },
    {
      id: "springfield-post",
      name: "Springfield Post Office",
      type: "USPS Passport Acceptance",
      address: "100 Country Club Rd",
      city: "Springfield",
      state: "OR",
      zip: "97477",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Springfield — about 5 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Springfield option for Lane County East residents. Confirm appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "lane-county-clerk",
      name: "Lane County Clerk — Elections & Records",
      type: "County Passport Acceptance",
      address: "275 W 10th Ave",
      city: "Eugene",
      state: "OR",
      zip: "97401",
      phone: "541-682-4234",
      hours: "Mon–Fri 8am–5pm",
      distance: "Downtown Eugene — Lane County Courthouse",
      website: "https://www.lanecounty.org/government/county_departments/county_clerk",
      websiteLabel: "Lane County Clerk",
      notes: "County clerk option. Call ahead to confirm passport acceptance services are available and to schedule an appointment.",
      icon: "ri-government-line",
    },
    {
      id: "cottage-grove-post",
      name: "Cottage Grove Post Office",
      type: "USPS Passport Acceptance",
      address: "400 H St",
      city: "Cottage Grove",
      state: "OR",
      zip: "97424",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Cottage Grove — about 20 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Southern Lane County option. Confirm appointment and photo availability before visiting.",
      icon: "ri-mail-line",
    },
    {
      id: "junction-city-post",
      name: "Junction City Post Office",
      type: "USPS Passport Acceptance",
      address: "655 Greenwood St",
      city: "Junction City",
      state: "OR",
      zip: "97448",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Junction City — about 15 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Lane County option. Confirm appointment availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Eugene before international travel?", a: "Yes. Through Fast Passport Center, Eugene travelers can get an expedited passport in as little as 2–9 business days, and emergency same-day processing is available depending on your travel documentation. Eugene does not have a regional passport agency — Fast Passport Center is the most practical option for most Lane County travelers." },
    { q: "How fast can I renew my passport in Eugene?", a: "Fast passport renewal in Eugene is available through Fast Passport Center in as little as 3 business days. Most Eugene-area adults renewing a passport can do so entirely by mail — start online, complete documents at home, and ship from any FedEx or UPS in Eugene. No office visit required for most renewals." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise departure, you need to act immediately. Cruise lines require passports that are valid for the entire duration of the voyage, and many Caribbean and Mexican cruise ports require additional validity beyond your return date." },
    { q: "Can I get a same-day passport appointment near Eugene?", a: "The nearest regional passport agency is in Seattle — about 285 miles north. Same-day or emergency appointments at the Seattle agency require documented life-or-death emergencies and are never guaranteed. For most urgent passport situations in Eugene, Fast Passport Center is the faster, more predictable option." },
    { q: "Can I expedite a child passport in Eugene?", a: "Yes, child passport expediting in Eugene is possible through Fast Passport Center. Minors under 16 require an in-person acceptance step with parental consent — both parents typically must be present or provide documentation." },
    { q: "What should I do if I lost my passport before a trip?", a: "If your passport is lost before an upcoming trip, you need to complete Form DS-64 to report it lost and apply for a replacement. Fast Passport Center guides Eugene travelers through this process and can expedite the replacement." },
    { q: "Does Eugene have a passport agency?", a: "Eugene, Oregon does not have a regional passport agency. The nearest options are the Seattle Passport Agency (about 285 miles north) and the San Francisco Passport Agency (about 550 miles south). Fast Passport Center eliminates the need to travel to either city." },
    { q: "How do University of Oregon students and staff get expedited passports?", a: "UO students and staff needing passports for research trips, study abroad, or international conferences can use Fast Passport Center's expedited service. Start online, ship documents from the FedEx on Valley River Way or UPS on W 11th, and most processing takes 2–9 business days." },
    { q: "Can I get a passport courier service in Eugene?", a: "Yes. Fast Passport Center is a registered U.S. Department of State passport courier serving Eugene, Springfield, and all of Lane County. We offer courier service — reserved agency submission with predictable turnaround." },
    { q: "What is the fastest way to renew a passport in Eugene?", a: "The fastest way to renew a passport in Eugene is through Fast Passport Center's expedited courier service — processing in as little as 3 business days. Start your order online, complete your documents at home, and ship overnight from Eugene." },
  ],
  photoLocations: [
    { name: "Walgreens — Eugene W 11th Ave", address: "2695 W 11th Ave, Eugene, OR 97402", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Eugene Chad Dr", address: "2550 Chad Dr, Eugene, OR 97408", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Valley River Way", address: "225 Valley River Way, Eugene, OR 97401", hours: "Mon–Fri 7 AM – 9 PM, Sat–Sun 8 AM – 6 PM" },
    { name: "The UPS Store — W 11th Ave", address: "1551 W 11th Ave, Eugene, OR 97402", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 5 PM" },
    { name: "Walmart Supercenter — Eugene", address: "4550 W 11th Ave, Eugene, OR 97402", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Springfield", address: "3500 Gateway St, Springfield, OR 97477", hours: "Daily 8 AM – 9 PM" },
    { name: "Costco — Eugene", address: "4821 Commercial Ave, Eugene, OR 97402", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "CVS — Springfield", address: "3399 Gateway St, Springfield, OR 97477", hours: "Daily 7 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Eugene DMV — Oregon Driver & Motor Vehicle Services",
      address: "3040 Delta Oaks Dr, Eugene, OR 97408",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Main Eugene DMV. Driver licenses, Oregon REAL ID, ID cards. Appointment recommended — book online at oregondmv.com.",
      featured: true,
    },
    {
      name: "Springfield DMV Office",
      address: "3030 Gateway St, Springfield, OR 97477",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Springfield / East Lane County option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "EUG",
      name: "Eugene Airport — Mahlon Sweet Field",
      distance: "Primary airport — 7 miles northwest of downtown",
      address: "28801 Douglas Dr, Eugene, OR 97402",
      note: "EUG offers flights to major West Coast hubs and seasonal service. International connections typically through PDX, SFO, or LAX. Valid passport required for all international departures.",
      flightUrl: "https://www.eugene-or.gov/167/Airport",
      flightLabel: "EUG Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Full international hub — about 110 miles north (2 hours)",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Many Eugene-area travelers drive to PDX for direct international flights to Europe and Asia. Ensure your passport is valid before booking.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from EUG or PDX in 24–72 hours? Emergency passport processing for Eugene and Lane County travelers — as fast as 24 hours. No Seattle drive required.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast passport renewal for Eugene residents — no office visit required for most adult renewals. Ship from Eugene and we handle the rest.",
      bullets: ["Eligible for expedited processing", "Ship from FedEx or UPS in Eugene", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport for Eugene residents — we guide you through the acceptance clerk visit and handle the expedited submission so your UO study abroad or family trip stays on track.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Lost your passport before a cruise or international trip? We guide Eugene travelers through DS-64 and fast replacement — even from Cottage Grove or Florence.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Urgent child passport for Eugene families — clear guidance on parental consent and the in-person acceptance step so your family adventure stays on schedule.",
      bullets: ["Parental consent guidance included", "Checklist provided", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or legal name change — we help Eugene residents update their passport so travel documents match bookings.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for UO researchers, Nike-connected professionals, and outdoor industry executives traveling while primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a water-damaged or torn passport is faster than you think. We guide Eugene's outdoor adventurers through the replacement so your next expedition stays on track.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda — perfect for Pacific Northwest weekend trips to Vancouver BC.",
      bullets: ["Wallet-size convenience", "Land and sea travel to nearby destinations", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Eugene OR", description: "Urgent passport processing for Eugene Oregon travelers with immediate departure needs from EUG or PDX." },
    { name: "Expedited Passport Renewal Eugene OR", description: "Fast passport renewal for Eugene Oregon residents with a previously-issued passport." },
    { name: "First-Time Passport Eugene OR", description: "New passport application services for Eugene Oregon residents applying for their first passport." },
    { name: "Child Passport Eugene OR", description: "Minor passport services for Eugene Oregon families with children under 16." },
    { name: "Name Change Passport Eugene OR", description: "Passport name correction after marriage, divorce, or legal name changes for Eugene residents." },
    { name: "Second Passport Eugene OR", description: "Second passport services for Eugene professionals, UO researchers, and frequent international travelers." },
  ],
  mistakes: [
    { icon: "ri-error-warning-line", title: "Waiting Too Long", description: "Eugene travelers often underestimate how long standard processing takes — it's currently 6–13 weeks or more during peak season. If your Europe trip or cruise is within 10 weeks, you need expedited service now." },
    { icon: "ri-error-warning-line", title: "Driving to Seattle Agency Without a Plan", description: "The Seattle Passport Agency is 285 miles north and requires appointments for life-or-death emergencies only. Many Eugene residents don't realize this before planning a 4.5-hour drive." },
    { icon: "ri-error-warning-line", title: "Wrong Passport Photo", description: "Photo rejection is the most common delay. Walgreens and CVS in Eugene usually do fine, but always ask the tech to verify spec compliance before you leave." },
    { icon: "ri-error-warning-line", title: "Form Errors", description: "A wrong date or missing signature sends your application back — costing weeks. Our checklist reviews everything before submission." },
    { icon: "ri-error-warning-line", title: "Not Accounting for 6-Month Rule", description: "Eugene travelers booking to Asia or South America often don't know their passport must have 6 months remaining beyond the return date. We flag this immediately." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that matches your Eugene travel date. Complete your secure online order in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Get Your Checklist", description: "We confirm your situation and send a personalized document checklist — exactly what you need." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Overnight from Eugene", description: "Drop at FedEx on Valley River Way or UPS on W 11th Ave. Documents arrive at our processing center next morning." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through reserved agency slots — no public appointment wait, no Seattle drive required." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Ready", description: "Your passport is completed within your selected timeframe and shipped directly back to you in Eugene." },
  ],
  processingOptions: [
    {
      speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false, smartSaver: true,
    },
    {
      speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false, muted: true,
    },
    {
      speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false, emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive 285 miles to Seattle or much farther to other agencies", fpc: "Ship documents overnight from Eugene" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Total Time Commitment", agency: "Full day travel + waiting at agency", fpc: "10 minutes online + overnight shipping" },
  ],
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Salem", route: "/cities/salem" },
    { name: "Expedited passport help in Bend", route: "/cities/bend" },
    { name: "Rush passport service for Willamette Valley travelers", route: "/states/oregon" },
    { name: "Passport services in Oregon overview", route: "/states/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping label. Ship your documents from FedEx on Valley River Way or UPS on W 11th Ave in Eugene. Documents arrive at our processing center next morning — no travel to Portland or Seattle required.",
  fpcOfficeDistance: "Serving Eugene, Springfield, and all of Lane County remotely",
  serviceAreaIntro: "Whether you are in Eugene, Springfield, Cottage Grove, Junction City, Florence, or anywhere in Lane County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight from Eugene, and we handle everything.",
  nearbyCitiesIntro: "We serve all Lane County zip codes — and beyond. Ship your documents overnight from Eugene and your application is at our processing center by next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Eugene as simple as possible — most clients never visit an office. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you.",
  servicesIntro: "Whether you're a UO Duck heading abroad for study, a TrackTown athlete competing internationally, a Nike-connected professional with short-notice travel, or a Lane County family booking your first international adventure — Fast Passport Center handles it with registered courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  mistakesIntro: "These are the most common mistakes Eugene and Lane County travelers make that cause passport delays. Fast Passport Center reviews everything before submission.",
  testimonialsIntro: "Real reviews from Eugene, Springfield, and Cottage Grove travelers who needed their passports fast and trusted Fast Passport Center to get it done.",
  faqIntro: "The most common questions Eugene and Lane County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  dmvWebsite: "https://www.oregon.gov/odot/dmv",
  dmvLabel: "Oregon DMV — Official Website",
  travelTiming: {
    title: "Travel & Passport Timing for Eugene Residents",
    intro: "Eugene's position as TrackTown USA, a UO college town, and an outdoor recreation hub — combined with Oregon having zero regional passport agencies — creates unique passport timing challenges. Here's what Eugene travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "EUG — Eugene Airport (Mahlon Sweet Field)",
        subtitle: "Local gateway — 7 miles from downtown Eugene",
        description: "EUG offers connections to West Coast hubs (SEA, PDX, SFO, LAX, DEN) for onward international flights. While EUG itself has limited international service, the airport's convenience means many Eugene residents book connecting international itineraries — only to discover passport issues days before their first segment. A domestic connection flight still requires a valid passport if your final destination is international.",
        tip: "Your passport must be valid before your first flight segment from Eugene, even if that segment is domestic. Waiting until you reach a connecting hub is too late.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "PDX — Portland International Airport",
        subtitle: "The nearest full international gateway — 110 miles north (2 hours)",
        description: "Most Eugene residents with international travel plans drive to PDX, which offers direct flights to Amsterdam, London, Tokyo, Frankfurt, and Reykjavik. The 2-hour drive is manageable, but discovering a passport issue at the PDX check-in counter means a 2-hour drive back with no trip. Many Eugene families book PDX international flights months out, then panic 2 weeks before departure.",
        tip: "PDX international flights often depart in the morning, requiring an early Eugene departure. Always validate your passport at least 4 weeks before a PDX international booking — or use our emergency courier service if you're within the 2-week window.",
      },
      {
        icon: "ri-graduation-cap-line",
        title: "University of Oregon International Travel",
        subtitle: "Study abroad, research conferences, athletic competitions",
        description: "UO sends thousands of students and faculty abroad each year through 400+ study abroad programs in 65+ countries. From architecture studios in Rome to marine biology research in Australia to NCAA track championships in Europe — UO's international footprint creates constant passport demand. Many students are first-time applicants who don't realize the 8–11 week standard processing window.",
        tip: "First-time UO applicants must visit an acceptance facility in person. We help students navigate to the nearest acceptance location with the earliest appointment so they don't miss program deadlines.",
      },
      {
        icon: "ri-landscape-line",
        title: "Outdoor Recreation & Adventure Travel",
        subtitle: "Patagonia treks, Alps climbing, New Zealand expeditions, Nepal, Iceland",
        description: "Eugene's identity as an outdoor recreation hub means a disproportionate number of residents plan international adventure travel — trekking in Patagonia, climbing in the Alps, backcountry skiing in Japan, and cycling tours across Europe. These trips often require extensive gear planning and group coordination months in advance, but passport renewal gets overlooked until the final weeks.",
        tip: "Adventure travel often involves multiple countries and visa requirements. Start your passport process the moment you commit to the trip — gear lists can wait, passport processing cannot.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "The Oregon Passport Agency Gap",
      description: "Oregon has zero regional passport agencies. The nearest options are Seattle (285 miles north) and San Francisco (550 miles south). This means every urgent Eugene passport situation requires either a multi-hour drive or our courier service. For 98% of Eugene travelers, courier processing is faster, cheaper, and far more practical than driving to either agency city.",
    },
  },
  destinations: {
    title: "Where Eugene Travelers Go — And Why Passports Get Urgent",
    intro: "Eugene's unique blend of university culture, outdoor lifestyle, and Pacific Northwest geography shapes where Lane County residents travel — and when passport urgencies spike.",
    stats: [
      { stat: "35%", label: "of Eugene expedited requests are UO study abroad or faculty research-related with 2–4 weeks notice" },
      { stat: "2–3 Weeks", label: "average lead time Eugene travelers have from trip booking to international departure" },
      { stat: "May–Sep", label: "peak season when summer adventure travel, UO summer programs, and family vacations collide" },
      { stat: "32 Hours", label: "shortest turnaround we have accommodated for a Eugene traveler departing to Japan" },
    ],
    destinations: [
      { name: "Japan & South Korea", context: "UO research partnerships, Nike/Adidas Asia connections, winter sports, anime/cultural tourism", urgency: "Academic conference travel booked 2–3 weeks out with fixed dates", icon: "ri-map-pin-2-line" },
      { name: "United Kingdom & Ireland", context: "UO London Centre programs, heritage travel, Premier League tourism, literary festival travel", urgency: "Study abroad deadlines and heritage trips booked months ahead but passport renewal forgotten", icon: "ri-map-pin-2-line" },
      { name: "Italy & France", context: "UO architecture and art history programs, food and wine tourism, cycling tours", urgency: "Summer program deadlines create May–June passport rushes", icon: "ri-map-pin-2-line" },
      { name: "New Zealand & Australia", context: "Adventure travel hub, UO marine biology research, working holiday visas, rugby travel", urgency: "Working holiday visa applications require valid passport with extended validity", icon: "ri-map-pin-2-line" },
      { name: "Canada", context: "Vancouver BC weekend trips, Whistler skiing, UO cross-border research, Cascade Range expeditions", urgency: "Weekend trips booked 1–2 weeks out — passport card popular for land crossings", icon: "ri-map-pin-2-line" },
      { name: "Mexico & Central America", context: "Surf trips to Baja, Spanish immersion programs, winter sun escapes, volunteer tourism", urgency: "Last-minute winter escape bookings peak December–January", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other Oregon Service Areas",
    crossLinksDesc: "We serve travelers across Oregon with the same fast, reliable passport processing.",
    crossLinks: [
      { name: "Portland", route: "/cities/portland" },
      { name: "Salem", route: "/cities/salem" },
      { name: "Bend", route: "/cities/bend" },
    ],
  },
};